import React, { useState, useEffect, useRef } from 'react';
import { Play, Upload, Save, Settings, ChevronDown, ChevronUp, Loader2, XCircle } from 'lucide-react';
import { API_URL } from '../../config';

export default function VideoPlayground() {
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [videoResult, setVideoResult] = useState(null);
  const [error, setError] = useState(null);
  const [showParams, setShowParams] = useState(true);
  
  const [imageUrl, setImageUrl] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [prompt, setPrompt] = useState('walking forward slowly, looking at camera, subtle smile');
  const [paramsText, setParamsText] = useState('{}');
  
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchModels();
  }, []);

  useEffect(() => {
    if (selectedModel) {
      setParamsText(JSON.stringify(selectedModel.params || {}, null, 2));
    }
  }, [selectedModel]);

  const fetchModels = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/admin/video-models?include_inactive=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setModels(data.models || []);
      if (data.models?.length > 0) {
        setSelectedModel(data.models[0]);
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    
    try {
      const token = localStorage.getItem('snapshot_token');
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/upload/temp`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setImageUrl(data.url);
      }
    } catch (err) {
      console.error('Upload error:', err);
    }
  };

  const handleGenerate = async () => {
    if (!imageUrl || !selectedModel) {
      setError('Bitte Bild und Modell auswählen');
      return;
    }
    
    setGenerating(true);
    setGenerationStatus('Starte Generation...');
    setVideoResult(null);
    setError(null);
    
    try {
      const token = localStorage.getItem('snapshot_token');
      
      let params = {};
      try {
        params = JSON.parse(paramsText);
      } catch (e) {
        setError('Ungültiges JSON in Parametern');
        setGenerating(false);
        return;
      }
      
      const res = await fetch(`${API_URL}/api/admin/video-models/playground/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model_key: selectedModel.key,
          image_url: imageUrl,
          prompt: prompt,
          params: params
        })
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Generation fehlgeschlagen');
      }
      
      const data = await res.json();
      
      if (data.status === 'completed') {
        setVideoResult(data.video_url);
        setGenerationStatus('Fertig!');
        setGenerating(false);
      } else if (data.request_id) {
        setGenerationStatus('In Warteschlange...');
        pollForResult(data.request_id, data.model_id);
      }
      
    } catch (err) {
      setError(err.message);
      setGenerationStatus(null);
      setGenerating(false);
    }
  };

  const pollForResult = async (requestId, modelId) => {
    const token = localStorage.getItem('snapshot_token');
    let attempts = 0;
    const maxAttempts = 120;
    
    const poll = async () => {
      attempts++;
      setGenerationStatus(`Generiere... (${attempts * 5}s)`);
      
      try {
        const res = await fetch(`${API_URL}/api/admin/video-models/playground/status/${requestId}?model_id=${encodeURIComponent(modelId)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        const data = await res.json();
        
        if (data.status === 'completed' && data.video_url) {
          setVideoResult(data.video_url);
          setGenerationStatus('Fertig!');
          setGenerating(false);
          return;
        } else if (data.status === 'failed') {
          setError(data.error || 'Generation fehlgeschlagen');
          setGenerating(false);
          return;
        }
        
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          setError('Timeout - Generation dauert zu lange');
          setGenerating(false);
        }
      } catch (err) {
        setError(err.message);
        setGenerating(false);
      }
    };
    
    setTimeout(poll, 5000);
  };

  const handleSaveAsDefault = async () => {
    if (!selectedModel) return;
    
    try {
      const token = localStorage.getItem('snapshot_token');
      const params = JSON.parse(paramsText);
      
      const res = await fetch(`${API_URL}/api/admin/video-models/${selectedModel.key}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ params })
      });
      
      if (res.ok) {
        alert('Parameter gespeichert!');
        fetchModels();
      }
    } catch (err) {
      alert('Fehler: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#10B981]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">🎮 Video Playground</h2>
        <p className="text-white/60 text-sm mt-1">Teste Modelle und optimiere Parameter - dann speichere als Default</p>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 flex items-center gap-2">
          <XCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="bg-black/30 border border-white/10 rounded-xl p-4">
            <label className="block text-white/70 text-sm mb-2">Modell auswählen</label>
            <select
              value={selectedModel?.key || ''}
              onChange={(e) => setSelectedModel(models.find(m => m.key === e.target.value))}
              className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none"
            >
              {models.map(m => (
                <option key={m.key} value={m.key}>
                  {m.label} {!m.is_active && '(inaktiv)'} - {m.price_snaps}💎
                </option>
              ))}
            </select>
            {selectedModel && (
              <div className="mt-2 text-sm text-white/50">
                <code className="bg-white/5 px-2 py-1 rounded">{selectedModel.model_id}</code>
              </div>
            )}
          </div>

          <div className="bg-black/30 border border-white/10 rounded-xl p-4">
            <label className="block text-white/70 text-sm mb-2">Test-Bild</label>
            <div className="flex gap-4">
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="w-32 h-32 border-2 border-dashed border-white/20 rounded-xl flex items-center justify-center cursor-pointer hover:border-[#10B981]/50 transition-all overflow-hidden"
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <Upload className="w-8 h-8 text-white/30" />
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <div className="flex-1">
                <input
                  type="text"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="Oder URL eingeben..."
                  className="w-full px-4 py-2 bg-black border border-white/20 rounded-xl text-white text-sm focus:border-[#10B981] focus:outline-none"
                />
                <p className="text-white/40 text-xs mt-2">Bild hochladen oder URL einfügen</p>
              </div>
            </div>
          </div>

          <div className="bg-black/30 border border-white/10 rounded-xl p-4">
            <label className="block text-white/70 text-sm mb-2">Prompt</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              placeholder="walking forward, looking at camera..."
              className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white focus:border-[#10B981] focus:outline-none resize-none"
            />
          </div>

          <div className="bg-black/30 border border-white/10 rounded-xl overflow-hidden">
            <button
              onClick={() => setShowParams(!showParams)}
              className="w-full px-4 py-3 flex items-center justify-between text-white/70 hover:bg-white/5 transition-all"
            >
              <span className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Parameter (JSON)
              </span>
              {showParams ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showParams && (
              <div className="p-4 border-t border-white/10">
                <textarea
                  value={paramsText}
                  onChange={(e) => setParamsText(e.target.value)}
                  rows={12}
                  className="w-full px-4 py-3 bg-black border border-white/20 rounded-xl text-white font-mono text-sm focus:border-[#10B981] focus:outline-none resize-none"
                />
                <p className="text-white/40 text-xs mt-2">
                  Diese Parameter werden an FAL gesendet. Bearbeite sie hier und speichere als Default.
                </p>
              </div>
            )}
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating || !imageUrl}
            className="w-full py-4 bg-[#10B981] text-black font-bold rounded-xl hover:bg-[#10B981]/90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {generationStatus || 'Generiere...'}
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Test generieren
              </>
            )}
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-black/30 border border-white/10 rounded-xl p-4 min-h-[400px] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Ergebnis</h3>
              {generationStatus && (
                <span className={"text-sm " + (generationStatus.includes('Fertig') ? 'text-green-400' : 'text-white/50')}>
                  {generationStatus}
                </span>
              )}
            </div>
            
            {videoResult ? (
              <div className="flex-1 flex flex-col">
                <div className="relative aspect-[9/16] max-h-[500px] bg-black rounded-xl overflow-hidden mx-auto">
                  <video
                    ref={videoRef}
                    src={videoResult}
                    controls
                    autoPlay
                    loop
                    playsInline
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="mt-4 flex gap-2">
                  <a
                    href={videoResult}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 py-2 text-center text-white/70 hover:text-white border border-white/20 rounded-xl hover:bg-white/5 transition-all"
                  >
                    🔗 Video öffnen
                  </a>
                  <button
                    onClick={handleSaveAsDefault}
                    className="flex-1 py-2 bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30 rounded-xl hover:bg-[#10B981]/30 transition-all flex items-center justify-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Als Default speichern
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-white/30">
                {generating ? (
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
                    <p>{generationStatus}</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <Play className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Klicke "Test generieren" um ein Video zu erstellen</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedModel && (
            <div className="bg-black/30 border border-white/10 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-white/70 mb-2">Modell-Info</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-white/50">Gruppe:</div>
                <div className="text-white">{selectedModel.group}</div>
                <div className="text-white/50">Preis:</div>
                <div className="text-white">{selectedModel.price_snaps} 💎 (${selectedModel.price_usd})</div>
                <div className="text-white/50">Status:</div>
                <div className={selectedModel.is_active ? 'text-green-400' : 'text-red-400'}>
                  {selectedModel.is_active ? '✅ Aktiv' : '❌ Inaktiv'}
                </div>
                {selectedModel.description && (
                  <>
                    <div className="text-white/50">Beschreibung:</div>
                    <div className="text-white">{selectedModel.description}</div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
